
import { Link } from 'react-router-dom';

export default function BehavioralEconomicsPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-rose-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-600 via-red-600 to-rose-600 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link to="/motivation" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
              <i className="ri-arrow-left-line mr-2"></i>
              動機付け一覧に戻る
            </Link>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl mb-6">
              <i className="ri-line-chart-line text-4xl"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">行動経済学</h1>
            <p className="text-xl text-white/90">Behavioral Economics</p>
          </div>
        </div>
      </section>

      {/* Core Message */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl shadow-2xl p-8 md:p-12 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">人は常に最適判断をしない。デフォルト・比較・視認性が行動を左右する</h2>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* 主要な概念 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-orange-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-brain-line text-xl text-white"></i>
                </div>
                行動経済学の主要概念
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-checkbox-blank-circle-fill text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">デフォルト効果</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    人は初期設定（デフォルト）をそのまま選びやすい
                  </p>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-6 border border-red-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-scales-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">比較効果</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    人は絶対値より相対的な比較で判断する
                  </p>
                </div>
                <div className="bg-gradient-to-br from-rose-50 to-orange-50 rounded-xl p-6 border border-rose-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-rose-600 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-eye-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">視認性</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    目に入りやすいものが選ばれやすい
                  </p>
                </div>
              </div>
            </div>

            {/* 薬局運営への活用 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-orange-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-hospital-line text-xl text-white"></i>
                </div>
                薬局運営への活用
              </h2>
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-route-line text-orange-600 mr-2"></i>
                    業務導線設計
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    行動経済学の知見を活かして、効率的で間違いの少ない業務導線を設計します。
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-orange-100">
                      <h4 className="font-bold text-gray-900 mb-2 text-sm">デフォルト効果の活用</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>・よく使う設定をデフォルトに</li>
                        <li>・安全な選択肢を初期値に</li>
                        <li>・推奨手順を標準化</li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-red-100">
                      <h4 className="font-bold text-gray-900 mb-2 text-sm">視認性の活用</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>・重要情報を目立つ位置に</li>
                        <li>・警告は赤色で表示</li>
                        <li>・よく使う物を手の届く場所に</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-6 border border-red-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-error-warning-line text-red-600 mr-2"></i>
                    チェック漏れ・実施率低下の対策
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    「気をつけて」という注意喚起ではなく、行動経済学に基づいた対策を講じます。
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start p-3 bg-white rounded-lg border border-red-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <div>
                        <span className="text-gray-700 text-sm font-semibold">チェックリストの強制表示</span>
                        <p className="text-xs text-gray-500">次の作業に進む前に確認画面を表示</p>
                      </div>
                    </div>
                    <div className="flex items-start p-3 bg-white rounded-lg border border-red-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <div>
                        <span className="text-gray-700 text-sm font-semibold">視覚的なリマインダー</span>
                        <p className="text-xs text-gray-500">作業場所に目立つ注意書きを配置</p>
                      </div>
                    </div>
                    <div className="flex items-start p-3 bg-white rounded-lg border border-red-100">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3 mt-0.5"></i>
                      <div>
                        <span className="text-gray-700 text-sm font-semibold">比較情報の提示</span>
                        <p className="text-xs text-gray-500">「他の店舗では実施率90%」など社会的比較を活用</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 認知バイアスへの対処 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-orange-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-mental-health-line text-xl text-white"></i>
                </div>
                認知バイアスへの対処
              </h2>
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
                <p className="text-gray-700 leading-relaxed mb-4">
                  人間には様々な認知バイアス（思考の偏り）があります。これを理解し、対策を講じることが重要です。
                </p>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4 border border-orange-100">
                    <h4 className="font-bold text-gray-900 mb-2 text-sm">確証バイアス</h4>
                    <p className="text-xs text-gray-600 mb-2">自分の信念を裏付ける情報ばかり集めてしまう</p>
                    <p className="text-xs text-orange-600">対策：反対意見を積極的に聞く仕組みを作る</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-red-100">
                    <h4 className="font-bold text-gray-900 mb-2 text-sm">現状維持バイアス</h4>
                    <p className="text-xs text-gray-600 mb-2">変化を避け、現状を維持しようとする</p>
                    <p className="text-xs text-red-600">対策：小さな変化から始め、成功体験を積む</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-rose-100">
                    <h4 className="font-bold text-gray-900 mb-2 text-sm">損失回避バイアス</h4>
                    <p className="text-xs text-gray-600 mb-2">得をすることより損を避けることを重視する</p>
                    <p className="text-xs text-rose-600">対策：変化しないことのリスクも明示する</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 明日の一手 */}
            <div className="bg-gradient-to-br from-orange-600 via-red-600 to-rose-600 rounded-2xl shadow-2xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <i className="ri-calendar-check-line text-3xl mr-3"></i>
                明日の一手
              </h2>
              <p className="text-lg text-white/90 leading-relaxed mb-6">
                業務の中で「デフォルト」「視認性」「比較」を活用できる場面を探してみましょう。
              </p>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6">
                <p className="text-white font-semibold mb-2">具体的なアクション：</p>
                <p className="text-white/90 leading-relaxed">
                  よく使う設定や手順を見直し、最も安全で効率的な選択肢がデフォルトになっているか確認する。
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-8 border-t border-gray-200">
              <Link to="/motivation/nudge" className="flex items-center text-orange-600 hover:text-orange-700 font-semibold transition-colors">
                <i className="ri-arrow-left-line mr-2"></i>
                ナッジ理論
              </Link>
              <Link to="/motivation/equity" className="flex items-center text-orange-600 hover:text-orange-700 font-semibold transition-colors">
                公平性理論
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
